/**
 * Webhook Triggers documentation page content.
 *
 * Inbound webhooks that START runs — distinct from outbound webhook events.
 * Covers both task-level and teammate-level webhook triggers.
 */

export const webhookTriggersContent = `
> **These are inbound webhook triggers that start runs.** For outbound webhook events that notify your server when runs complete, see [Webhook Events](/docs/webhooks-guide). They are two completely different features.

## Overview

Webhook triggers let external systems start runs via HTTP POST. There are two types:

| Type | What it triggers | Payload |
|------|-----------------|---------|
| **Task webhook** | Runs a pre-created task | Optional JSON body (task has its own instructions) |
| **Teammate webhook** | Starts a new dynamic run | Requires \`{"message": "..."}\` in body |

**Rule of thumb:** If the work is always the same, use a **task webhook**. If each trigger has a different message, use a **teammate webhook**.

## Task Webhook Triggers

Attach a webhook trigger to a task. When you POST to the generated URL, the task runs automatically using its pre-defined instructions.

### Create a Task Webhook

\`\`\`python
from m8tes import M8tes

client = M8tes()

# Create a task first
task = client.tasks.create(
    teammate_id=1,
    instructions="Generate the weekly sales report",
    name="Weekly Report",
)

# Add a webhook trigger
trigger = client.tasks.triggers.create(
    task_id=task.id,
    type="webhook",
)
print(trigger.url)  # https://m8tes.ai/api/v1/webhooks/tasks/{id}/{token}
\`\`\`

\`\`\`bash
curl -X POST https://m8tes.ai/api/v2/tasks/1/triggers \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"type": "webhook"}'
\`\`\`

### Trigger the Task

POST to the webhook URL. The JSON body is **optional** — the task already has its own instructions.

\`\`\`bash
# Simple trigger (no body needed)
curl -X POST https://m8tes.ai/api/v1/webhooks/tasks/1/tok_abc123

# With optional context
curl -X POST https://m8tes.ai/api/v1/webhooks/tasks/1/tok_abc123 \\
  -H "Content-Type: application/json" \\
  -d '{"context": "Include data from last 7 days"}'
\`\`\`

## Teammate Webhook Triggers

Enable a webhook directly on a teammate. Each POST creates a new run with the message you provide — useful for dynamic, event-driven workflows.

### Enable Teammate Webhook

\`\`\`python
webhook = client.teammates.enable_webhook(teammate_id=1)
print(webhook.url)  # https://m8tes.ai/api/v1/webhooks/mates/1/tok_...
\`\`\`

\`\`\`bash
curl -X POST https://m8tes.ai/api/v2/teammates/1/webhook \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

> **Important:** The webhook URL contains a secret token and is only shown once. Store it securely.

### Trigger a Run

POST a JSON body with a \`message\` field:

\`\`\`bash
curl -X POST https://m8tes.ai/api/v1/webhooks/mates/1/tok_abc123 \\
  -H "Content-Type: application/json" \\
  -d '{"message": "New support ticket: User cannot reset password"}'
\`\`\`

The teammate processes the message using its configured instructions and tools.

### Disable Teammate Webhook

\`\`\`python
client.teammates.disable_webhook(teammate_id=1)
\`\`\`

\`\`\`bash
curl -X DELETE https://m8tes.ai/api/v2/teammates/1/webhook \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

## When to Use Which

| Use Case | Type |
|----------|------|
| Automated report on a schedule + manual trigger | Task webhook |
| CI/CD pipeline triggers a fixed analysis | Task webhook |
| Slack/Discord bot forwards messages | Teammate webhook |
| Customer support system routes tickets | Teammate webhook |
| GitHub webhook on new issues | Teammate webhook |

## Managing Triggers

### List Triggers

\`\`\`python
triggers = client.tasks.triggers.list(task_id=1)
for t in triggers:
    print(t.id, t.type, t.url)
\`\`\`

### Delete a Trigger

\`\`\`python
client.tasks.triggers.delete(task_id=1, trigger_id=10)
\`\`\`

## Deduplication

Include an \`X-M8tes-Event-Id\` header to prevent duplicate runs from retried webhooks:

\`\`\`bash
curl -X POST https://m8tes.ai/api/v1/webhooks/mates/1/tok_abc123 \\
  -H "Content-Type: application/json" \\
  -H "X-M8tes-Event-Id: evt_unique_123" \\
  -d '{"message": "Process this event"}'
\`\`\`

## What's Next

- [Scheduling](/docs/scheduling) — automate with cron and intervals
- [Email Inbox](/docs/email-inbox) — trigger runs from inbound emails
- [Webhook Events](/docs/webhooks-guide) — get notified when runs complete (outbound)
`;
